import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import projectsData from '../../data/projects';
import { saveAs } from 'file-saver';

const ManagerContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
`;

const Title = styled.h2`
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.darkGray};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.primary ? props.theme.colors.accent : props.theme.colors.lightGray};
  color: ${props => props.primary ? props.theme.colors.white : props.theme.colors.darkGray};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  font-weight: 500;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.short};
  
  &:hover {
    background-color: ${props => props.primary ? props.theme.colors.accentDark : props.theme.colors.mediumGray};
  }
`;

const ProjectsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 1rem;
  background-color: ${props => props.theme.colors.lightGray};
  color: ${props => props.theme.colors.darkGray};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.lightGray};
  
  &:hover {
    background-color: ${props => props.theme.colors.offWhite};
  }
`;

const TableCell = styled.td`
  padding: 1rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  margin-right: 0.5rem;
  background-color: transparent;
  color: ${props => props.delete ? 'tomato' : props.theme.colors.accent};
  border: 1px solid ${props => props.delete ? 'tomato' : props.theme.colors.accent};
  border-radius: ${props => props.theme.borderRadius.small};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.short};
  
  &:hover {
    background-color: ${props => props.delete ? 'tomato' : props.theme.colors.accent};
    color: ${props => props.theme.colors.white};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 2rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.colors.darkGray};
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.small};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.small};
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const TagsInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.small};
`;

const Tag = styled.span`
  background-color: ${props => props.theme.colors.lightGray};
  color: ${props => props.theme.colors.darkGray};
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.borderRadius.small};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const DeleteTagButton = styled.span`
  cursor: pointer;
  font-weight: bold;
  &:hover {
    color: tomato;
  }
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const ImagePreview = styled.div`
  width: 150px;
  height: 150px;
  border: 1px solid ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.small};
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DeleteImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background-color: tomato;
  }
`;

const FileUploadContainer = styled.div`
  margin-top: 1rem;
`;

const FileUploadInput = styled.input`
  display: none;
`;

const FileUploadLabel = styled.label`
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: ${props => props.theme.colors.lightGray};
  color: ${props => props.theme.colors.darkGray};
  border-radius: ${props => props.theme.borderRadius.small};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.short};
  
  &:hover {
    background-color: ${props => props.theme.colors.mediumGray};
    color: ${props => props.theme.colors.white};
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.success ? '#d4edda' : props.error ? '#f8d7da' : props.theme.colors.lightGray};
  color: ${props => props.success ? '#155724' : props.error ? '#721c24' : props.theme.colors.darkGray};
`;

const ProjectManager = () => {
  const [projects, setProjects] = useState(projectsData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);
  const fileInputRef = useRef(null);
  
  // Load projects from localStorage on component mount if available
  useEffect(() => {
    const savedProjects = localStorage.getItem('projectsData');
    if (savedProjects) {
      try {
        setProjects(JSON.parse(savedProjects));
      } catch (error) {
        console.error('Failed to parse saved projects:', error);
      }
    }
  }, []);

  // Save projects to localStorage whenever they change
  useEffect(() => {
    if (projects) {
      // Save to localStorage
      localStorage.setItem('projectsData', JSON.stringify(projects));
      
      // Add a console log to confirm the save
      console.log('Projects saved to localStorage:', projects);
    }
  }, [projects]);
  
  // Open modal for adding a new project
  const handleAddProject = () => {
    setCurrentProject({
      id: Date.now(), // Temporary ID, would be replaced by database ID in real app
      title: '',
      description: '',
      fullDescription: '',
      images: [],
      tags: [],
      url: ''
    });
    setIsModalOpen(true);
  };
  
  // Open modal for editing an existing project
  const handleEditProject = (project) => {
    setCurrentProject({ ...project });
    setIsModalOpen(true);
  };
  
  // Handle deletion of a project
  const handleDeleteProject = (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      const updatedProjects = projects.filter(project => project.id !== id);
      
      // Update state
      setProjects(updatedProjects);
      
      // Immediately save to localStorage to ensure sync
      localStorage.setItem('projectsData', JSON.stringify(updatedProjects));
      
      showStatusMessage(`Project deleted successfully`, 'success');
    }
  };
  
  const showStatusMessage = (message, type = 'info') => {
    setStatusMessage({ message, type });
    setTimeout(() => setStatusMessage(null), 5000);
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentProject({ ...currentProject, [name]: value });
  };
  
  // Add a new tag to the project
  const handleAddTag = (e) => {
    e.preventDefault();
    if (newTag.trim() && !currentProject.tags.includes(newTag.trim())) {
      setCurrentProject({
        ...currentProject,
        tags: [...currentProject.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };
  
  // Remove a tag from the project
  const handleRemoveTag = (tagToRemove) => {
    setCurrentProject({
      ...currentProject,
      tags: currentProject.tags.filter(tag => tag !== tagToRemove)
    });
  };
  
  // Add a new image URL to the project
  const handleAddImageUrl = (e) => {
    e.preventDefault();
    if (newImageUrl.trim() && !currentProject.images.includes(newImageUrl.trim())) {
      setCurrentProject({
        ...currentProject,
        images: [...currentProject.images, newImageUrl.trim()]
      });
      setNewImageUrl('');
    }
  };
  
  // Handle file upload
  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    Array.from(files).forEach(file => {
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        showStatusMessage(`${file.name} is not an image file`, 'error');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        // Add the data URL to the project images
        setCurrentProject({
          ...currentProject,
          images: [...currentProject.images, event.target.result]
        });
      };
      reader.readAsDataURL(file);
    });
    
    // Clear the file input
    e.target.value = null;
  };
  
  // Remove an image from the project
  const handleRemoveImage = (imageToRemove) => {
    setCurrentProject({
      ...currentProject,
      images: currentProject.images.filter(image => image !== imageToRemove)
    });
  };
  
  // Save the project (new or edited)
  const handleSaveProject = (e) => {
    e.preventDefault();
    
    // Validation
    if (!currentProject.title.trim() || !currentProject.description.trim()) {
      showStatusMessage('Title and description are required', 'error');
      return;
    }
    
    // Check if this is a new project or an edit
    const isNewProject = !projects.find(p => p.id === currentProject.id);
    
    if (isNewProject) {
      // Add new project
      setProjects([...projects, currentProject]);
      showStatusMessage('Project added successfully', 'success');
    } else {
      // Update existing project
      setProjects(projects.map(p => 
        p.id === currentProject.id ? currentProject : p
      ));
      showStatusMessage('Project updated successfully', 'success');
    }
    
    // Close modal
    setIsModalOpen(false);
  };
  
  // Export projects as JSON file
  const handleExportProjects = () => {
    const jsonData = JSON.stringify(projects, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    saveAs(blob, 'projects-data.json');
    showStatusMessage('Projects exported successfully', 'success');
  };
  
  // Import projects from JSON file
  const handleImportProjects = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedProjects = JSON.parse(event.target.result);
        if (Array.isArray(importedProjects)) {
          setProjects(importedProjects);
          showStatusMessage('Projects imported successfully', 'success');
        } else {
          showStatusMessage('Invalid projects data format', 'error');
        }
      } catch (error) {
        showStatusMessage('Failed to parse JSON file', 'error');
        console.error(error);
      }
    };
    reader.readAsText(file);
    
    // Clear the file input
    e.target.value = null;
  };
  
  // Add the reset function
  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset to default projects? This will delete all your changes.')) {
      // Import the module fresh to get the latest changes
      import('../../data/defaultProjects.js')
        .then(module => {
          const freshDefaultProjects = module.default;
          console.log("Loading fresh default projects:", freshDefaultProjects);
          setProjects(freshDefaultProjects);
          localStorage.setItem('projectsData', JSON.stringify(freshDefaultProjects));
          showStatusMessage('Projects reset to defaults', 'success');
        })
        .catch(err => {
          console.error("Error loading default projects:", err);
          showStatusMessage('Failed to reset projects', 'error');
        });
    }
  };
  
  return (
    <ManagerContainer>
      <Title>Project Manager</Title>
      
      <ButtonContainer>
        <div>
          <Button primary onClick={handleAddProject} style={{ marginRight: '1rem' }}>Add New Project</Button>
          <Button onClick={handleExportProjects} style={{ marginRight: '1rem' }}>Export Projects</Button>
          <FileUploadLabel htmlFor="import-projects" style={{ marginRight: '1rem' }}>
            Import Projects
            <FileUploadInput 
              id="import-projects" 
              type="file" 
              accept=".json" 
              onChange={handleImportProjects} 
            />
          </FileUploadLabel>
          <Button onClick={handleResetToDefaults}>Reset to Defaults</Button>
        </div>
      </ButtonContainer>
      
      {statusMessage && (
        <StatusMessage success={statusMessage.type === 'success'} error={statusMessage.type === 'error'}>
          {statusMessage.message}
        </StatusMessage>
      )}
      
      <ProjectsTable>
        <thead>
          <tr>
            <TableHeader>ID</TableHeader>
            <TableHeader>Title</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader>Tags</TableHeader>
            <TableHeader>Images</TableHeader>
            <TableHeader>URL</TableHeader>
            <TableHeader>Actions</TableHeader>
          </tr>
        </thead>
        <tbody>
          {projects.map(project => (
            <TableRow key={project.id}>
              <TableCell>{project.id}</TableCell>
              <TableCell>{project.title}</TableCell>
              <TableCell>{project.description.substring(0, 50)}...</TableCell>
              <TableCell>{project.tags.join(', ')}</TableCell>
              <TableCell>{project.images.length}</TableCell>
              <TableCell>{project.url ? 'Yes' : 'No'}</TableCell>
              <TableCell>
                <ActionButton onClick={() => handleEditProject(project)}>Edit</ActionButton>
                <ActionButton delete onClick={() => handleDeleteProject(project.id)}>Delete</ActionButton>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </ProjectsTable>
      
      {isModalOpen && (
        <Modal>
          <ModalContent>
            <Title>{currentProject.id ? 'Edit Project' : 'Add New Project'}</Title>
            
            <Form onSubmit={handleSaveProject}>
              <FormGroup>
                <Label>Title</Label>
                <Input 
                  type="text" 
                  name="title" 
                  value={currentProject.title} 
                  onChange={handleInputChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Short Description</Label>
                <Input 
                  type="text" 
                  name="description" 
                  value={currentProject.description} 
                  onChange={handleInputChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Full Description</Label>
                <Textarea 
                  name="fullDescription" 
                  value={currentProject.fullDescription} 
                  onChange={handleInputChange} 
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Project URL (for "Try Now" button)</Label>
                <Input 
                  type="url" 
                  name="url" 
                  value={currentProject.url || ''} 
                  onChange={handleInputChange}
                  placeholder="https://example.com" 
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Tags</Label>
                <TagsInput>
                  {currentProject.tags.map(tag => (
                    <Tag key={tag}>
                      {tag}
                      <DeleteTagButton onClick={() => handleRemoveTag(tag)}>×</DeleteTagButton>
                    </Tag>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Input 
                      type="text" 
                      value={newTag} 
                      onChange={(e) => setNewTag(e.target.value)} 
                      placeholder="Add a tag..." 
                      style={{ flex: 1, margin: 0, padding: '0.25rem 0.5rem' }}
                    />
                    <Button type="button" onClick={handleAddTag}>+</Button>
                  </div>
                </TagsInput>
              </FormGroup>
              
              <FormGroup>
                <Label>Images</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Input 
                    type="text" 
                    value={newImageUrl} 
                    onChange={(e) => setNewImageUrl(e.target.value)} 
                    placeholder="Add image URL..." 
                    style={{ flex: 1 }}
                  />
                  <Button type="button" onClick={handleAddImageUrl}>Add URL</Button>
                </div>
                
                <FileUploadContainer>
                  <FileUploadLabel htmlFor="image-upload">
                    Upload Image
                    <FileUploadInput 
                      id="image-upload" 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      onChange={handleImageUpload} 
                      ref={fileInputRef}
                    />
                  </FileUploadLabel>
                </FileUploadContainer>
                
                <ImagePreviewContainer>
                  {currentProject.images.map((image, index) => (
                    <ImagePreview key={index}>
                      <img src={image} alt={`Preview ${index + 1}`} />
                      <DeleteImageButton onClick={() => handleRemoveImage(image)}>×</DeleteImageButton>
                    </ImagePreview>
                  ))}
                </ImagePreviewContainer>
              </FormGroup>
              
              <ButtonContainer>
                <Button type="button" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                <Button primary type="submit">Save Project</Button>
              </ButtonContainer>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </ManagerContainer>
  );
};

export default ProjectManager; 